import { StripeError, StripePaymentMethod } from 'ha/modules/StripeCheckout';
import { MakeCall } from 'ha/services/apiV2/createMakeCall';
import { UserApiV2 } from 'ha/services/apiV2/types/User';
import { Currency } from 'ha/constants/Currencies';

import {
  PaymentRequestCategory,
  PaymentRequestState,
  PaymentRequestStatus,
  PaymentRequestUserType,
} from './types';

export interface PaymentRequest {
  amount: string;
  cancelledAt?: string;
  category: PaymentRequestCategory;
  conversation_id?: number;
  createdAt: string;
  createdBy?: string;
  currency: Currency;
  description?: string;
  dueDate?: string;
  engagement?: PaymentRequestEngagement;
  engagement_uuid?: string;
  handle: string;
  id: string;
  landlord?: UserApiV2;
  listing?: PaymentRequestListing;
  paidAt?: string;
  payment_plan_id?: string;
  payout_date: string;
  platformUserID: number;
  receiptURL?: string;
  sender?: string;
  senderAvatarURL?: string;
  state: PaymentRequestState;
  tenant_id?: number;
  tenant_uuid?: string;
  title: string;
}

export type PaymentRequestListing = {
  address: string;
  alias?: string;
  city: string;
  country: string;
  id: number;
  imageURL?: string;
  latitude: number;
  longitude: number;
  street: string;
  uuid: string;
};

export type PaymentRequestEngagement = {
  moveIn: string;
  moveOut: string;
  uuid: string;
};

export interface PaymentIntentServerResponse extends PaymentRequest {
  error: StripeError;
  requires_action: boolean;
  payment_intent_client_secret?: string;
}

export interface CreatePaymentRequestFormData {
  amount: string;
  conversation_id?: number;
  currency: Currency;
  description?: string;
  due_date?: string;
  tenant_id?: number;
  tenant_uuid?: string;
  title: string;
  category?: PaymentRequestCategory;
}

export interface CreatePaymentRequestResponse
  extends Omit<PaymentRequest, 'sender' | 'senderAvatarURL'> {
  tenant_id: number; // 0 if no tenant
  tenant_uuid: string; // empty string if no tenant
  conversation_id: number; // 0 if no conversation
}

export interface PayReqConversationParams {
  conversation_id: number;
  tenant_id: number;
  tenant_uuid: string;
}

export interface PaymentRequestsResponse {
  data: PaymentRequest[];
  hasMore: boolean;
  total_count: number;
}

export interface StripeToken {
  card?: {
    brand: string;
    country: string;
    cvc_check?: string;
    exp_month: number;
    exp_year: number;
    funding: string;
    id: string;
    last4: string;
  };
  client_ip: string;
  created: number;
  id: string;
  livemode: boolean;
  object: string;
  type: string;
  used: boolean;
}

export type IdealSourceStatus =
  | 'source.chargeable'
  | 'source.failed'
  | 'source.canceled'
  | 'custom.created'
  | 'custom.succeeded'
  | 'custom.failed'
  | 'charge.succeeded';

interface IdealSource {
  id: string;
  client_secret: string;
  amount: number;
  currency: string; // 'eur'
  statement_descriptor?: string;
  flow: 'redirect';
  object: 'source';
  livemode: boolean; // should be `true` on production
  bank_info: {
    bank?: string | null;
    bic?: string | null;
    iban_last4?: string | null;
    statement_descriptor?: string | null;
  };
}

interface StripeIdealConfirmation {
  source: string;
  client_secret: string;
}

export const getPaymentRequests = (makeCall: MakeCall) => (filters?: {
  limit?: number;
  offset?: number;
  state?: PaymentRequestStatus;
  user_type?: PaymentRequestUserType;
}) =>
  makeCall<PaymentRequestsResponse>('GET', '/api/payments/requests/me/v1', {
    query: {
      limit: 20,
      user_type: 'author',
      ...filters,
    },
  });

export const getPaymentRequestByHandle = (makeCall: MakeCall) => (
  handle: string,
) => makeCall<PaymentRequest>('GET', `/api/payments/requests/v1/${handle}`);

export const cancelPaymentRequest = (makeCall: MakeCall) => (handle: string) =>
  makeCall<{}>('DELETE', `/api/payments/requests/v1/${handle}`);

export const submitIdealSource = (makeCall: MakeCall) => (
  handle: string,
  payload: IdealSource,
) =>
  makeCall<{ success: boolean }>(
    'PATCH',
    `/api/payments/requests/v2/${handle}/ideal`,
    { data: payload },
  );

export const checkIdealStatus = (makeCall: MakeCall) => (
  handle: string,
  { source, client_secret }: StripeIdealConfirmation,
) =>
  makeCall<{ status: IdealSourceStatus }>(
    'GET',
    `/api/payments/requests/v2/${handle}/ideal/source/${source}/secret/${client_secret}`,
  );

export const payPaymentRequestV2 = (makeCall: MakeCall) => (
  handle: string,
  paymentMethod: Pick<StripePaymentMethod, 'id' | 'card' | 'livemode'>,
) =>
  makeCall<PaymentIntentServerResponse>(
    'PATCH',
    `/api/payments/requests/v2/${handle}`,
    {
      data: {
        card: paymentMethod.card,
        livemode: paymentMethod.livemode,
        payment_method_id: paymentMethod.id,
      },
    },
  );

export const payPaymentRequestV2Confirm = (makeCall: MakeCall) => (
  handle: string,
  paymentIntentId: string,
) =>
  makeCall<{ success: boolean }>(
    'PATCH',
    `/api/payments/requests/v2/${handle}/confirm`,
    { data: { payment_intent_id: paymentIntentId } },
  );

export type GetPaymentRequests = ReturnType<typeof getPaymentRequests>;
export type GetPaymentRequestByHandle = ReturnType<
  typeof getPaymentRequestByHandle
>;

export type PayPaymentRequestV2 = ReturnType<typeof payPaymentRequestV2>;
export type CancelPaymentRequest = ReturnType<typeof cancelPaymentRequest>;

export type SubmitIdealSource = ReturnType<typeof submitIdealSource>;
export type CheckIdealStatus = ReturnType<typeof checkIdealStatus>;
